import { LANGUAGE_EN } from '@home/_config/config';
import Abnormal from '@home/components/Abnormal';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { Col, Row, SearchBox } from 'tea-component';
import { useIntl, } from 'umi';
import AgentHomeSearchIcon from '../svg/agent-home-search.svg';
import AgentCard from './components/AgentCard';
import CategoryTabs from '../SkillsCollection/components/CategoryTabs';
import { useAgentModelsData } from './hooks';
import { EAgentCategory, AGENT_CATEGORY_MAP } from './types';
import './index.scss';
import noDataIcon from '@/pages/aigc/assets/svg/agent-no-data.svg';

/**
 * Agent 集合组件
 */
const AgentCollectionPage = () => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;

  // 搜索关键词
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  // 当前激活的分类
  const [activeCategory, setActiveCategory] = useState<string>(EAgentCategory.ALL);
  // 数据来源
  const { allAgents, loading, selectedAgentId } = useAgentModelsData();

  // 分类 Tabs（直接从映射表生成）
  const categoryTabs = useMemo(() => {
    return Object.entries(AGENT_CATEGORY_MAP).map(([key, config]) => ({
      key,
      label: isEN ? config.labelEn : config.labelZh,
    }));
  }, [isEN]);

  // 过滤后的 Agent 列表（支持分类 + 搜索双重过滤）
  const filteredAgents = useMemo(() => {
    let result = allAgents || [];

    // 分类过滤（二分法兜底：数据Agent 精确匹配，其余一律归入通用对话）
    if (activeCategory === EAgentCategory.DATA_AGENT) {
      result = result.filter(agent => agent.category === EAgentCategory.DATA_AGENT);
    } else if (activeCategory === EAgentCategory.GENERAL_CHAT) {
      result = result.filter(agent => agent.category !== EAgentCategory.DATA_AGENT);
    }

    // 搜索过滤
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      result = result.filter(agent => {
        if (isEN) {
          return agent.name.toLowerCase().includes(keyword) ||
                    agent.description.toLowerCase().includes(keyword);
        } 
        return agent.name_cn.toLowerCase().includes(keyword) ||
                    agent.description_cn.toLowerCase().includes(keyword);
      });
    }

    return result;
  }, [searchKeyword, isEN, allAgents, activeCategory]);

  // 处理搜索变化
  const handleSearchChange = (value: string) => {
    setSearchKeyword(value);
  };

  return (
    <div className={classNames('dba-agent-collection')}>

      {/* 标题 */}
      <div className="dba-agent-collection__header">
        <h1 className="dba-agent-collection__title">
          {formatMessage({ id: 'dba_collection_title' })}
        </h1>
      </div>

      {/* 工具栏：左侧 CategoryTabs + 右侧搜索框 */}
      <div className="dba-agent-collection__toolbar">
        <div className="dba-agent-collection__tabs-wrap">
          <CategoryTabs
            activeCategory={activeCategory}
            onChange={setActiveCategory}
            tabs={categoryTabs}
          />
        </div>
        <div className="dba-agent-collection__search dba-agent-collection__search--toolbar">
          <img src={AgentHomeSearchIcon} alt="" className="dba-agent-collection__search-icon" />
          <SearchBox
            className="dba-agent-search-box"
            placeholder={formatMessage({ id: 'dba_collection_search_placeholder' })}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <Abnormal
        isLoading={loading}
        isNoData={isEmpty(allAgents)}
        isError={false}
        component={
          <>

            {/* Agent 列表 */}
            <div className="dba-agent-collection__list">
              <Row>
                {filteredAgents?.map((agent) => (
                  <Col key={agent.agentId} span={8}>
                    <AgentCard
                      agent={agent}
                      isSelected={agent.agentId === selectedAgentId}
                      searchKeyword={searchKeyword}
                    />
                  </Col>
                ))}
              </Row>

              {/* 空状态 */}
              {filteredAgents.length === 0 && (
                <div className="dba-agent-collection__empty">
                  <img src={noDataIcon} alt="NoDataIcon" className="game-icon" />
                  <p>{formatMessage({ id: 'dba_collection_empty' })}</p>
                </div>
              )}
            </div>
          </>
        }
      />

    </div>
  );
};

export default AgentCollectionPage;
